import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Occurrence } from '../../../core/occurrence/occurrence.models';
import type { EChartsCoreOption } from 'echarts/core';
import { SCOPE_SUB_SCOPES } from '../../../core/occurrence/occurrence-catalog';
import {
  COMPLAINT_REASON_LABELS, EFFECTIVENESS_LABELS, NATURE_LABELS,
  ORIGIN_LABELS, SCOPE_OPTIONS, STATE_LABELS, SUB_SCOPE_LABELS, TYPE_LABELS,
} from '../../../core/occurrence/occurrence.labels';
import {
  DeadlineItem, OccurrenceOrigin, OccurrenceScope, OccurrenceStatistics as Stats, OccurrenceSubScope,
  OccurrenceType,
} from '../../../core/occurrence/occurrence.models';
import { OccurrenceService } from '../../../core/occurrence/occurrence.service';
import { PageHeader } from '../../../shared/page-header/page-header';
import { EChart, EChartPoint } from '../../../shared/echart/echart';

/**
 * O recorte que um pedaço de gráfico representa, tal como a listagem o entende.
 *
 * <p>Vai pendurado em cada ponto e volta intacto no clique — é o que transforma um "quantos"
 * num "quais" sem a página ter de adivinhar o que foi clicado a partir do rótulo.
 */
export interface DrillFilters {
  readonly type?: string;
  readonly origin?: string;
  readonly nature?: string;
  readonly state?: string;
  readonly situation?: string;
  readonly complaintReason?: string;
  readonly effectiveness?: string;
  readonly onTime?: boolean;
  readonly year?: number;
}

/** Cores por estado (coerentes com os badges da lista/detalhe). */
const STATE_COLORS: Record<string, string> = {
  REGISTADO: '#6e84a3',
  EM_ANALISE: '#2c7be5',
  EM_TRATAMENTO: '#f6c343',
  EM_MONITORIZACAO: '#39afd1',
  AVALIACAO_REAVALIACAO: '#fd7e14',
};

/** Paleta categórica da marca (navy primeiro). */
const CATEGORY_COLORS = ['#12263f', '#2c7be5', '#39afd1', '#00b27a', '#f6c343', '#fd7e14', '#e63757', '#6e84a3',
  '#95aac9', '#1c3a5e', '#a6c5f7', '#d2ddec'];

@Component({
  selector: 'app-occurrence-statistics',
  imports: [FormsModule, RouterLink, PageHeader, EChart],
  templateUrl: './occurrence-statistics.html',
  styleUrl: './occurrence-statistics.scss',
})
export class OccurrenceStatistics implements OnInit, OnDestroy {
  private readonly service = inject(OccurrenceService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly stats = signal<Stats | null>(null);
  protected readonly loading = signal(true);
  /** A recarregar com dados já no ecrã: os gráficos ficam, só esmorecem enquanto esperam. */
  protected readonly refreshing = signal(false);
  protected readonly error = signal<string | null>(null);

  protected readonly scopeOptions = SCOPE_OPTIONS;
  protected readonly currentYear = new Date().getFullYear();
  protected year: number = this.currentYear;
  protected scope: OccurrenceScope | null = null;
  protected subScope: OccurrenceSubScope | null = null;

  /** Tema escuro ativo? Reconstrói as options (cores de texto/eixos) quando muda. */
  protected readonly dark = signal(document.documentElement.classList.contains('dark-mode'));
  private readonly themeObserver = new MutationObserver(() =>
    this.dark.set(document.documentElement.classList.contains('dark-mode')));

  protected readonly subScopeFilterOptions = computed(() => {
    if (!this.scope) return [];
    return SCOPE_SUB_SCOPES[this.scope].map((value) => ({ value, label: SUB_SCOPE_LABELS[value] }));
  });

  /** Anos disponíveis (derivados dos dados de todos os anos). */
  protected readonly years = computed(() => {
    // Os anos já vistos ficam: com um âmbito sem dados num ano, esse ano desaparecia da lista e o
    // seletor ficava em branco, com as estatísticas desse mesmo ano no ecrã.
    const years = new Set([
      ...this.seenYears(), ...(this.stats()?.byTypeAndYear ?? []).map((item) => item.year),
      this.currentYear, this.year,
    ]);
    return [...years].sort((a, b) => b - a);
  });
  private readonly seenYears = signal<number[]>([]);

  // --- Detalhe de uma métrica: que ocorrências estão por trás do pedaço clicado
  protected readonly drillOpen = signal(false);
  protected readonly drillTitle = signal('');
  protected readonly drillFilters = signal<DrillFilters>({});
  protected readonly drillLoading = signal(false);
  protected readonly drillItems = signal<Occurrence[]>([]);
  protected readonly drillTotal = signal(0);

  /** Quantas se mostram na modal. Acima disto, diz-se o total e fica-se por aqui. */
  private static readonly DRILL_LIMIT = 50;

  /**
   * Abre o detalhe do pedaço clicado.
   *
   * <p>Os filtros do pedaço juntam-se aos da página (ano, âmbito): o gráfico já estava a
   * contar dentro desse recorte, e a lista tem de contar o mesmo — senão os números não batem
   * certo com o que se acabou de ver.
   */
  protected onChartPoint(point: EChartPoint, tituloDoCartao: string): void {
    const drill = point.payload as DrillFilters | undefined;
    if (!drill || !Object.keys(drill).length) return;

    // Séries sem nome vêm com o nome interno do ECharts ("series N"); não se mostra a
    // ninguém. E "Ocorrências" é o nome da série do combo — repeti-lo no título não diz nada.
    const anonima = (nome?: string) => !nome || nome.startsWith('series') || nome === 'Ocorrências';
    const partes = [point.seriesName, point.name].filter((parte) => !anonima(parte));
    this.drillTitle.set(`${tituloDoCartao} · ${partes.join(' — ') || point.name}`);
    this.drillFilters.set(drill);
    this.drillOpen.set(true);
    this.drillLoading.set(true);
    this.drillItems.set([]);

    this.service.list({
      year: drill.year ?? this.year,
      scope: this.scope,
      subScope: this.subScope,
      type: drill.type,
      origin: drill.origin,
      nature: drill.nature,
      state: drill.state,
      situation: drill.situation,
      complaintReason: drill.complaintReason,
      effectiveness: drill.effectiveness,
      onTime: drill.onTime,
      size: OccurrenceStatistics.DRILL_LIMIT,
    }).subscribe({
      next: (page) => {
        this.drillItems.set(page.content);
        this.drillTotal.set(page.totalElements);
        this.drillLoading.set(false);
      },
      error: () => {
        this.drillItems.set([]);
        this.drillTotal.set(0);
        this.drillLoading.set(false);
      },
    });
  }

  protected closeDrill(): void {
    this.drillOpen.set(false);
  }

  /** Quantas ficaram de fora do que a modal mostra. */
  protected readonly drillHidden = computed(
    () => Math.max(0, this.drillTotal() - this.drillItems().length));

  ngOnInit(): void {
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    this.readUrl();
    this.load();
  }

  ngOnDestroy(): void {
    this.themeObserver.disconnect();
  }

  /** Ano, âmbito e subâmbito vivem no endereço: sobrevivem ao refresh e ao link partilhado. */
  private readUrl(): void {
    const q = this.route.snapshot.queryParamMap;
    const year = Number(q.get('ano'));
    if (Number.isInteger(year) && year > 1900 && year < 3000) this.year = year;
    const scope = q.get('ambito') as OccurrenceScope | null;
    if (scope && SCOPE_OPTIONS.some((option) => option.value === scope)) {
      this.scope = scope;
      const sub = q.get('subambito') as OccurrenceSubScope | null;
      if (sub && SCOPE_SUB_SCOPES[scope].includes(sub)) this.subScope = sub;
    }
  }

  private writeUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route, replaceUrl: true, queryParamsHandling: 'merge',
      queryParams: {
        ano: this.year === this.currentYear ? null : this.year,
        ambito: this.scope,
        subambito: this.subScope,
      },
    });
  }

  protected onScopeChange(): void {
    this.subScope = null;
    this.load();
  }

  /**
   * Carrega as estatísticas.
   *
   * <p>Só a primeira vez mostra "a carregar" — a partir daí os gráficos ficam onde estão e os
   * dados novos entram por cima, animados. Trocá-los por um texto a cada mudança de filtro fazia
   * a página inteira piscar, e perdia-se a comparação entre o antes e o depois, que é
   * precisamente o que se quer ao mexer num filtro.
   */
  protected load(): void {
    this.writeUrl();
    const primeiraVez = this.stats() === null;
    this.loading.set(primeiraVez);
    this.refreshing.set(!primeiraVez);
    this.error.set(null);
    this.service.statistics(this.year, this.scope, this.subScope).subscribe({
      next: (stats) => {
        this.seenYears.update((seen) => [...new Set([...seen, ...stats.byTypeAndYear.map((item) => item.year)])]);
        this.stats.set(stats);
        this.loading.set(false);
        this.refreshing.set(false);
      },
      error: () => {
        this.error.set('Não foi possível carregar as estatísticas.');
        this.loading.set(false);
        this.refreshing.set(false);
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Options dos gráficos (recalculadas quando os dados ou o tema mudam)
  // ---------------------------------------------------------------------------

  private theme() {
    const dark = this.dark();
    return {
      text: dark ? '#d2ddec' : '#283e59',
      muted: dark ? '#6e84a3' : '#95aac9',
      axis: dark ? '#1e3a5c' : '#e3ebf6',
      primary: dark ? '#2c7be5' : '#12263f',
      tooltipBg: dark ? '#152e4d' : '#fff',
    };
  }

  private baseTooltip() {
    const theme = this.theme();
    return {
      backgroundColor: theme.tooltipBg,
      borderColor: theme.axis,
      textStyle: { color: theme.text },
    };
  }

  protected typeLabel(key: string): string {
    return TYPE_LABELS[key as OccurrenceType] ?? key;
  }

  protected natureLabel(key: string): string {
    return NATURE_LABELS[key as keyof typeof NATURE_LABELS] ?? key;
  }

  protected complaintReasonLabel(key: string): string {
    return COMPLAINT_REASON_LABELS[key as keyof typeof COMPLAINT_REASON_LABELS] ?? key;
  }

  protected effectivenessLabel(key: string): string {
    return EFFECTIVENESS_LABELS[key as keyof typeof EFFECTIVENESS_LABELS] ?? key;
  }

  /** Avaliações de eficácia feitas depois da data esperada. */
  protected readonly effectivenessLate = computed(() => {
    const stats = this.stats();
    return Math.max(0, (stats?.effectivenessDone ?? 0) - (stats?.effectivenessOnTime ?? 0));
  });

  protected originLabel(key: string): string {
    return ORIGIN_LABELS[key as OccurrenceOrigin] ?? key;
  }

  /** Rótulo curto para eixos (o nome completo fica no tooltip). */
  private shortLabel(label: string): string {
    const cut = label.split(' — ')[0].split(' (')[0];
    return cut.length > 18 ? `${cut.slice(0, 17)}…` : cut;
  }

  /** Donut genérico — a mesma forma serve origem, natureza e resultado da eficácia. */
  private donut(
    entries: readonly { name: string; value: number; color?: string; drill?: DrillFilters }[],
  ): EChartsCoreOption {
    const theme = this.theme();
    const ring = this.dark() ? '#152e4d' : '#fff';
    return {
      tooltip: { ...this.baseTooltip(), trigger: 'item' },
      series: [{
        type: 'pie',
        radius: ['42%', '68%'],
        center: ['50%', '52%'],
        data: entries.map((entry, index) => ({
          name: entry.name,
          value: entry.value,
          // Viaja intacto até ao clique: é por ele que se sabe que ocorrências pedir.
          drill: entry.drill,
          itemStyle: {
            color: entry.color ?? CATEGORY_COLORS[index % CATEGORY_COLORS.length],
            borderRadius: 8, borderColor: ring, borderWidth: 3,
          },
        })),
        label: { color: theme.text, fontSize: 11, formatter: '{b}\n{d}%' },
        labelLine: { lineStyle: { color: theme.muted } },
        emphasis: { scaleSize: 6 },
      }],
    };
  }

  /** Barras horizontais empilhadas dentro/fora do prazo — serve origem e tipo. */
  private deadlineBars(
    items: readonly DeadlineItem[],
    label: (key: string) => string,
    drillOf: (key: string, onTime: boolean) => DrillFilters,
  ): EChartsCoreOption {
    const theme = this.theme();
    const labelFmt = (params: { value: number }) => params.value > 0 ? String(params.value) : '';
    return {
      tooltip: { ...this.baseTooltip(), trigger: 'axis' },
      legend: { textStyle: { color: theme.text, fontSize: 11 }, top: 0 },
      grid: { left: 8, right: 24, top: 40, bottom: 8, containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: { color: theme.muted },
        splitLine: { lineStyle: { color: theme.axis } },
        minInterval: 1,
      },
      yAxis: {
        type: 'category',
        data: items.map((item) => this.shortLabel(label(item.key))),
        axisLabel: { color: theme.muted, fontSize: 10 },
        axisLine: { lineStyle: { color: theme.axis } },
        axisTick: { show: false },
      },
      series: [
        {
          name: 'Dentro do prazo',
          type: 'bar',
          stack: 'prazo',
          data: items.map((item) => ({ value: item.onTime, drill: drillOf(item.key, true) })),
          itemStyle: { color: '#00b27a', borderRadius: 6, borderColor: theme.tooltipBg, borderWidth: 1.5 },
          barMaxWidth: 22,
          label: { show: true, color: '#fff', fontSize: 10, formatter: labelFmt },
        },
        {
          name: 'Fora do prazo',
          type: 'bar',
          stack: 'prazo',
          data: items.map((item) => ({ value: item.total - item.onTime, drill: drillOf(item.key, false) })),
          itemStyle: { color: '#e63757', borderRadius: 6, borderColor: theme.tooltipBg, borderWidth: 1.5 },
          barMaxWidth: 22,
          label: { show: true, color: '#fff', fontSize: 10, formatter: labelFmt },
        },
      ],
    };
  }

  /** Donut: Nº de ações por origem, com % no rótulo (modelo "Origem" do exemplo). */
  protected readonly originDonut = computed<EChartsCoreOption>(() => {
    return this.donut((this.stats()?.byOrigin ?? []).map((item) => ({
      name: this.originLabel(item.key), value: item.count,
      drill: { origin: item.key },
    })));
  });

  /** Combo barras + linha %: tipo de ocorrência (modelo "Tipo de Ocorrência" do exemplo). */
  protected readonly typeCombo = computed<EChartsCoreOption>(() => {
    const stats = this.stats();
    const theme = this.theme();
    const items = [...(stats?.byType ?? [])].sort((a, b) => b.count - a.count);
    const total = items.reduce((sum, item) => sum + item.count, 0);
    return {
      tooltip: { ...this.baseTooltip(), trigger: 'axis' },
      grid: { left: 8, right: 28, top: 32, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        data: items.map((item) => this.shortLabel(this.typeLabel(item.key))),
        axisLabel: { color: theme.muted, fontSize: 10, interval: 0, rotate: items.length > 5 ? 28 : 0 },
        axisLine: { lineStyle: { color: theme.axis } },
        axisTick: { show: false },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: { color: theme.muted },
          splitLine: { lineStyle: { color: theme.axis } },
        },
        {
          type: 'value',
          axisLabel: { color: theme.muted, formatter: '{value}%' },
          splitLine: { show: false },
          max: 100,
        },
      ],
      series: [
        {
          name: 'Ocorrências',
          type: 'bar',
          data: items.map((item) => ({ value: item.count, drill: { type: item.key } })),
          itemStyle: { color: theme.primary, borderRadius: [10, 10, 0, 0] },
          barMaxWidth: 42,
          // Contagem BRANCA no topo da barra; a % da linha fica mais abaixo — sem sobreposição
          label: { show: true, position: 'insideTop', distance: 8, color: '#fff', fontWeight: 700 },
        },
        {
          name: '% do total',
          type: 'line',
          yAxisIndex: 1,
          data: items.map((item) => total ? Math.round((item.count / total) * 1000) / 10 : 0),
          smooth: true,
          itemStyle: { color: '#2c7be5' },
          lineStyle: { color: '#2c7be5', width: 2 },
          symbolSize: 7,
          // A linha corre sempre por cima das barras navy → branco é legível
          label: { show: true, position: 'top', color: '#fff', fontSize: 10, fontWeight: 600, formatter: '{c}%' },
          labelLayout: { moveOverlap: 'shiftY' },
        },
      ],
    };
  });

  /**
   * Barras empilhadas contra a origem. A dimensão escolhe-se no card: o documento pede o
   * cruzamento por tipo e, nas participações, por natureza — e o estado já lá estava.
   */
  protected crossDimension: 'state' | 'type' | 'nature' = 'type';

  protected readonly crossOriginStack = computed<EChartsCoreOption>(() => {
    const stats = this.stats();
    const theme = this.theme();
    const dimension = this.crossDimension;
    const rows = dimension === 'state'
      ? (stats?.byStateAndOrigin ?? []).map((row) => ({ key: row.state, origin: row.origin, count: row.count }))
      : dimension === 'type' ? (stats?.byTypeAndOrigin ?? []) : (stats?.byNatureAndOrigin ?? []);
    const label = (key: string) => dimension === 'state'
      ? (STATE_LABELS[key as keyof typeof STATE_LABELS] ?? key)
      : dimension === 'type' ? this.typeLabel(key) : this.natureLabel(key);
    const origins = [...new Set(rows.map((row) => row.origin))];
    const keys = dimension === 'state'
      ? Object.keys(STATE_COLORS).filter((state) => rows.some((row) => row.key === state))
      : [...new Set(rows.map((row) => row.key))];
    const color = (key: string, index: number) => dimension === 'state'
      ? STATE_COLORS[key]
      : CATEGORY_COLORS[index % CATEGORY_COLORS.length];
    return {
      tooltip: { ...this.baseTooltip(), trigger: 'axis' },
      legend: { textStyle: { color: theme.text, fontSize: 11 }, top: 0, type: 'scroll' },
      grid: { left: 8, right: 8, top: 48, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        data: origins.map((origin) => this.shortLabel(this.originLabel(origin))),
        axisLabel: { color: theme.muted, fontSize: 10, interval: 0, rotate: origins.length > 5 ? 28 : 0 },
        axisLine: { lineStyle: { color: theme.axis } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: theme.muted },
        splitLine: { lineStyle: { color: theme.axis } },
      },
      series: keys.map((key, index) => ({
        name: this.shortLabel(label(key)),
        type: 'bar',
        stack: 'cruzamento',
        data: origins.map((origin) => ({
          value: rows.find((row) => row.origin === origin && row.key === key)?.count ?? 0,
          drill: { origin, [dimension]: key } as DrillFilters,
        })),
        // Segmentos "pill": arredondados e com respiro entre si
        itemStyle: { color: color(key, index), borderRadius: 6, borderColor: theme.tooltipBg, borderWidth: 1.5 },
        barMaxWidth: 42,
        label: {
          show: true, color: '#fff', fontSize: 10,
          formatter: (params: { value: number }) => params.value > 0 ? String(params.value) : '',
        },
      })),
    };
  });

  /** Barras horizontais: fechadas dentro/fora do prazo de implementação, por origem. */
  protected readonly deadlineByOrigin = computed<EChartsCoreOption>(() =>
    this.deadlineBars(
      this.stats()?.closedWithinDeadlineByOrigin ?? [],
      (key) => this.originLabel(key),
      (key, onTime) => ({ origin: key, situation: 'FECHADO', onTime }),
    ));

  /** Donut: natureza da participação — é aqui que reclamações e denúncias se isolam. */
  protected readonly natureDonut = computed<EChartsCoreOption>(() => {
    return this.donut((this.stats()?.byNature ?? []).map((item) => ({
      name: this.natureLabel(item.key), value: item.count,
      drill: { nature: item.key },
    })));
  });

  /** Donut: resultado da avaliação da eficácia (eficaz, não eficaz, não aferido). */
  protected readonly effectivenessDonut = computed<EChartsCoreOption>(() => {
    const colors: Record<string, string> = {
      EFICAZ: '#00b27a',
      NAO_EFICAZ: '#e63757',
      NAO_APLICAVEL: '#95aac9',
      NAO_FOI_POSSIVEL_AFERIR: '#f6c343',
    };
    return this.donut((this.stats()?.byEffectiveness ?? []).map((item) => ({
      name: this.effectivenessLabel(item.key),
      value: item.count,
      color: colors[item.key],
      drill: { effectiveness: item.key },
    })));
  });

  /** Barras: motivo das reclamações (o documento pede-o isolado). */
  protected readonly complaintReasonBars = computed<EChartsCoreOption>(() => {
    const theme = this.theme();
    const items = [...(this.stats()?.byComplaintReason ?? [])].sort((a, b) => b.count - a.count);
    return {
      tooltip: { ...this.baseTooltip(), trigger: 'axis' },
      grid: { left: 8, right: 24, top: 16, bottom: 8, containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: { color: theme.muted },
        splitLine: { lineStyle: { color: theme.axis } },
        minInterval: 1,
      },
      yAxis: {
        type: 'category',
        data: items.map((item) => this.shortLabel(this.complaintReasonLabel(item.key))),
        axisLabel: { color: theme.muted, fontSize: 10 },
        axisLine: { lineStyle: { color: theme.axis } },
        axisTick: { show: false },
      },
      series: [{
        type: 'bar',
        data: items.map((item) => ({
          value: item.count,
          drill: { complaintReason: item.key, nature: 'RECLAMACAO' },
        })),
        itemStyle: { color: theme.primary, borderRadius: [0, 8, 8, 0] },
        barMaxWidth: 22,
        label: { show: true, position: 'right', color: theme.text, fontSize: 11 },
      }],
    };
  });

  /** Fechadas dentro/fora do prazo, por tipo — o mesmo corte do prazo, do lado do tipo. */
  protected readonly deadlineByType = computed<EChartsCoreOption>(() =>
    this.deadlineBars(
      this.stats()?.closedWithinDeadlineByType ?? [],
      (key) => this.typeLabel(key),
      (key, onTime) => ({ type: key, situation: 'FECHADO', onTime }),
    ));

  protected readonly typeByYearMultiples = computed<EChartsCoreOption>(() => {
    const stats = this.stats();
    const theme = this.theme();
    const rows = stats?.byTypeAndYear ?? [];
    const years = [...new Set(rows.map((row) => row.year))].sort((a, b) => a - b).slice(-4);
    const globalMax = Math.max(1, ...rows.filter((row) => years.includes(row.year)).map((row) => row.count));
    const grids: object[] = [];
    const xAxes: object[] = [];
    const yAxes: object[] = [];
    const series: object[] = [];
    const titles: object[] = [];
    years.forEach((year, index) => {
      const columns = years.length > 2 ? 2 : years.length;
      const col = index % columns;
      const row = Math.floor(index / columns);
      const width = columns === 1 ? 88 : 42;
      const left = 6 + col * 48;
      const top = 12 + row * 44;
      const yearRows = rows.filter((item) => item.year === year).sort((a, b) => b.count - a.count).slice(0, 6);
      grids.push({ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: '30%' });
      titles.push({
        text: String(year), left: `${left + width / 2}%`, top: `${top - 8}%`,
        textAlign: 'center', textStyle: { color: theme.text, fontSize: 13, fontWeight: 700 },
      });
      xAxes.push({
        type: 'category', gridIndex: index,
        data: yearRows.map((item) => this.shortLabel(this.typeLabel(item.key))),
        axisLabel: { color: theme.muted, fontSize: 9, interval: 0, rotate: 34 },
        axisLine: { lineStyle: { color: theme.axis } },
        axisTick: { show: false },
      });
      yAxes.push({
        type: 'value', gridIndex: index, max: globalMax,
        axisLabel: { color: theme.muted, fontSize: 9 },
        splitLine: { lineStyle: { color: theme.axis } },
        minInterval: 1,
      });
      series.push({
        type: 'bar', xAxisIndex: index, yAxisIndex: index,
        data: yearRows.map((item) => ({ value: item.count, drill: { type: item.key, year } })),
        itemStyle: { color: theme.primary, borderRadius: [8, 8, 0, 0] },
        barMaxWidth: 26,
        label: { show: true, position: 'top', color: theme.text, fontSize: 10, fontWeight: 700 },
      });
    });
    return {
      tooltip: { ...this.baseTooltip() },
      title: titles,
      grid: grids,
      xAxis: xAxes,
      yAxis: yAxes,
      series,
    };
  });
}
